import React, { useEffect, useState } from 'react';
import campaignService, { CampaignPreflightResponse, PreflightCheck } from '../../../services/campaignService';

interface Props {
  campaignId: string;
}

const levelClass = (level?: 'info' | 'warn' | 'crit') => {
  if (level === 'crit') return 'bg-red-100 text-red-800';
  if (level === 'warn') return 'bg-yellow-100 text-yellow-800';
  return 'bg-blue-100 text-blue-800';
};

const CampaignPreflightPanel: React.FC<Props> = ({ campaignId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const [data, setData] = useState<CampaignPreflightResponse | null>(null);

  const run = async () => {
    if (!campaignId) return;
    try {
      setLoading(true);
      setError(null);
      setPremiumBlocked(false);
      const resp = await campaignService.runPreflight(campaignId);
      setData(resp);
    } catch (e: any) {
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
        setPremiumBlocked(true);
      } else {
        setError('Failed to run preflight checks');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const readinessPill = (r?: CampaignPreflightResponse['readiness']) => {
    if (r === 'blocked') return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Blocked</span>;
    if (r === 'warnings') return <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">Warnings</span>;
    return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Ready</span>;
  };

  const renderCheck = (c: PreflightCheck, idx: number) => (
    <li key={`${c.code}-${idx}`} className="p-3 rounded border border-gray-200 flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-800">{c.label}</p>
        {c.value !== undefined && (
          <p className="text-xs text-gray-500 mt-1">Value: {typeof c.value === 'number' ? c.value.toFixed ? c.value.toFixed(2) : c.value : String(c.value)}</p>
        )}
      </div>
      <div className="flex items-center space-x-2">
        {c.level && (
          <span className={`px-2 py-1 rounded text-xs font-semibold ${levelClass(c.level)}`}>{c.level.toUpperCase()}</span>
        )}
        <span className={`px-2 py-1 rounded text-xs font-semibold ${c.pass === null ? 'bg-gray-100 text-gray-700' : c.pass ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {c.pass === null ? 'Unknown' : c.pass ? 'Pass' : 'Fail'}
        </span>
      </div>
    </li>
  );

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Campaign Preflight</h3>
        <button
          onClick={run}
          disabled={loading}
          className={`px-4 py-2 rounded text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {loading ? 'Checking…' : 'Run Preflight'}
        </button>
      </div>

      {premiumBlocked && (
        <div className="p-4 mb-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-yellow-700 text-sm">Premium feature. Please upgrade to run preflight checks.</p>
        </div>
      )}

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">Mailbox</p>
              <p className="text-lg font-semibold">{data.mailbox_id}</p>
              <p className="text-xs text-gray-400 mt-1">Domain: {data.domain || 'Unknown'}</p>
            </div>
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">Readiness</p>
              <div className="mt-1">{readinessPill(data.readiness)}</div>
            </div>
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">Recent Signals</p>
              <p className="text-xs text-gray-600 mt-1">Bounce: {data.rates.bounce_rate.toFixed(2)}% • Complaints: {data.rates.complaint_rate.toFixed(2)}%</p>
            </div>
          </div>

          <div>
            <h4 className="text-md font-semibold text-gray-800 mb-2">Checks</h4>
            <ul className="space-y-2">
              {data.checks.map(renderCheck)}
            </ul>
          </div>
        </div>
      )}

      {!loading && !data && !premiumBlocked && !error && (
        <div className="p-3 rounded border border-gray-200 text-sm text-gray-600">Run preflight to validate campaign settings and risk.</div>
      )}
    </div>
  );
};

export default CampaignPreflightPanel;
